/**
 * Fix markdown formatting by ensuring proper line breaks.
 * LLMs sometimes output markdown without proper newlines, which prevents rendering.
 * This function adds necessary line breaks before headers, bullets, list items,
 * and horizontal rules. Also fixes common LLM formatting mistakes.
 *
 * @param {string} text - The markdown text to fix
 * @returns {string} - The text with proper line breaks for markdown rendering
 */
export const fixMarkdownLineBreaks = (text) => {
  if (!text) return text;

  let result = text;

  // Strip code wrappers: <code>, </code>, ```html, ```
  result = result.replace(/^<code>\s*/i, '').replace(/\s*<\/code>$/i, '');
  result = result.replace(/^```html\s*/i, '').replace(/\s*```$/i, '');
  result = result.replace(/^```\s*/i, '');

  // Security: strip any script tags
  result = result.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Normalize line endings
  result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Fix LLM line-wrapping that splits words across lines
  // Pattern: word fragment + newlines + continuation (lowercase letter or 's')
  // "Policie\n\ns" -> "Policies"
  result = result.replace(/([a-zA-Z])\n+([a-z])/g, '$1$2');

  // Fix malformed headers: "#\n\n# " or "# \n\n# " -> "## "
  result = result.replace(/^#\s*\n+#\s+/gm, '## ');

  // Remove lines that are just hash symbols alone (malformed headers)
  result = result.replace(/^#+\s*$/gm, '');

  // Remove lines that are just asterisks or dashes alone (malformed separators/bullets)
  result = result.replace(/^\*+\s*$/gm, '');
  result = result.replace(/^-+\s*$/gm, '');

  // Fix "**Text:*" pattern (unbalanced bold) -> "**Text:**"
  // LLM outputs **bold:* instead of **bold:**
  result = result.replace(/\*\*([^*\n]+):\*(?!\*)/g, '**$1:**');

  // Fix "**Text*" pattern at end of line -> "**Text**"
  result = result.replace(/\*\*([^*\n]+)\*$/gm, '**$1**');

  // Fix section headers that start with ** and end with :* -> convert to ## header
  // "**Next Steps for You:*" -> "## Next Steps for You:"
  result = result.replace(/^\*\*([^*\n]+):\*\s*$/gm, '## $1:');

  // Fix bullet + bold pattern: "*   **Text:*" -> "* **Text:**"
  result = result.replace(/^\*\s+\*\*([^*\n]+):\*\s*$/gm, '* **$1:**');

  // Fix bullet + bold pattern: "*   **Text:**" -> "* **Text:**" (normalize spacing)
  result = result.replace(/^\*\s{2,}\*\*/gm, '* **');

  // Fix standalone ** or *** used as separators - convert to horizontal rule
  result = result.replace(/^\*{2,3}$/gm, '---');

  // Fix standalone -- used as separator - convert to horizontal rule
  result = result.replace(/^--$/gm, '---');

  // AGGRESSIVE BULLET FIX: Any line starting with whitespace + asterisk + letter/word
  // Convert to proper bullet: "    *Text" or "    * Text" -> "* Text"
  result = result.replace(/^[ \t]+\*\s*(\S)/gm, '* $1');

  // Same for dash bullets
  result = result.replace(/^[ \t]+-\s*(\S)/gm, '- $1');

  // Fix bullets at start of line without space: *Text -> * Text
  result = result.replace(/^\*([A-Za-z])/gm, '* $1');
  result = result.replace(/^-([A-Za-z])/gm, '- $1');

  // Ensure header at the very start of text has a newline after it for separation
  result = result.replace(/^(#{1,6}\s.+)(\n?)(?=[^\n])/gm, '$1\n\n');

  // Add blank line before headers (##, ###, etc.) if not already preceded by blank line
  result = result.replace(/([^\n])(#{1,6}\s)/g, '$1\n\n$2');
  result = result.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');

  // Add blank line before horizontal rules (---, ***, ___)
  result = result.replace(/([^\n])\n?(---+|___+|\*\*\*+)\n?/g, '$1\n\n$2\n\n');

  // Handle horizontal rule at start of line without preceding blank line
  result = result.replace(/^(---+|___+|\*\*\*+)$/gm, '\n$1\n');

  // Add blank line before bullet points (* or -) for list start
  result = result.replace(/([^\n\s])(\n?)([*-]\s)/g, (match, before, existingNewline, bullet) => {
    if (before === '\n') return match;
    return `${before}\n\n${bullet}`;
  });

  // Add blank line before numbered list items (1., 2., etc.) for list start
  result = result.replace(/([^\n\s])(\n?)(\d+\.\s)/g, (match, before, existingNewline, num) => {
    if (before === '\n') return match;
    return `${before}\n\n${num}`;
  });

  // Ensure consecutive list items only have single newline between them
  result = result.replace(/(\n[*-]\s.+)\n\n([*-]\s)/g, '$1\n$2');
  result = result.replace(/(\n\d+\.\s.+)\n\n(\d+\.\s)/g, '$1\n$2');

  // Clean up excessive newlines (more than 2 in a row)
  result = result.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace
  result = result.trim();

  return result;
};

export default fixMarkdownLineBreaks;
